import { useEffect, useState } from 'react';

interface BackgroundImageProps {
  backgroundImageUrl?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  backgroundOverlay?: boolean;
  backgroundOverlayOpacity?: number;
  backgroundEnabled?: boolean;
  emphasizeNav?: boolean;
}

const BackgroundImage = ({
  backgroundImageUrl,
  backgroundSize = 'cover',
  backgroundPosition = 'center',
  backgroundRepeat = 'no-repeat',
  backgroundOverlay = false,
  backgroundOverlayOpacity = 0.3,
  backgroundEnabled = true,
  emphasizeNav = true,
}: BackgroundImageProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Read current emphasizeNav from data attribute if present (optional)
  useEffect(() => {
    const emphasize = (window as any)?.__GX_EMPHASIZE_NAV__;
    if (emphasize === true) {
      document.body.classList.add('gx-nav-emphasis');
    } else if (emphasize === false) {
      document.body.classList.remove('gx-nav-emphasis');
    }
  }, []);

  useEffect(() => {
    if (!backgroundEnabled || !backgroundImageUrl) {
      // clear any previously applied styles
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
      document.body.classList.remove('gx-nav-emphasis');
      return;
    }

    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = backgroundImageUrl;
  }, [backgroundEnabled, backgroundImageUrl]);

  useEffect(() => {
    if (!backgroundEnabled || !backgroundImageUrl || !imageLoaded) return;

    // Set only the image on body (no overlay), so nav can show pure image
    document.body.style.backgroundImage = `url('${backgroundImageUrl}')`;
    document.body.style.backgroundSize = backgroundSize as string;
    document.body.style.backgroundPosition = backgroundPosition as string;
    document.body.style.backgroundRepeat = backgroundRepeat as string;
    document.body.style.backgroundAttachment = 'fixed';
    document.body.classList.add('gx-bg-enabled');

    // Manage overlay via body class and CSS variable; applied only to content via CSS
    if (backgroundOverlay) {
      document.body.classList.add('gx-bg-overlay');
      document.body.style.setProperty(
        '--gx-overlay-opacity',
        String(backgroundOverlayOpacity)
      );
    } else {
      document.body.classList.remove('gx-bg-overlay');
      document.body.style.removeProperty('--gx-overlay-opacity');
    }

    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundAttachment = '';
      document.body.classList.remove('gx-bg-enabled');
      document.body.classList.remove('gx-bg-overlay');
      document.body.style.removeProperty('--gx-overlay-opacity');
    };
  }, [
    backgroundEnabled,
    backgroundImageUrl,
    imageLoaded,
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
    backgroundOverlay,
    backgroundOverlayOpacity,
  ]);

  return null;
};

export default BackgroundImage;
